import { CloseOutlined, LikeOutlined, PlusCircleOutlined, ToolOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Avatar, Button, Popover, Tag } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { UserHoverCard } from '@/components'
import {
  GetUserSkillDocument,
  RequestedUserSkillFragment,
  useGetUserSkillQuery,
  useUserSkillUpdateMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'
import { ga, traceEvent } from '@/utils/ga'

import { UserInfoSectionLayout } from '../user-info-section-layout/user-info-section-layout.component'
import { UserInfoProps } from '../user-info.interface'
import s from '../user-info.module.less'
import { UserInfoSkillModal } from './user-info-skill-modal.component'

export const UserInfoSkillInner: React.FC<
  UserInfoProps & { bodyStyle?: any; skills: RequestedUserSkillFragment[] }
> = ({ skills, userId, viewerId, bodyStyle }) => {
  const history = useHistory()

  const [userSkillUpdate] = useUserSkillUpdateMutation({
    refetchQueries: [getOperationName(GetUserSkillDocument)!],
    onCompleted(value) {
      if (value.userSkillUpdate === 'added') {
        const category = traceEvent.profile
        ga('event', category.events.click_add_skills, {
          event_category: category.event_category,
          event_label: viewerId === userId ? '1' : '0',
        })
      }
    },
  })

  return (
    <div style={bodyStyle}>
      <div>
        <div className={s.skillAndEndorsements}>
          {skills.map((skill) => {
            const users = skill.evaluatedUsers?.edges.map(({ node }) => node) || []
            const isAgreed = users.some((user) => user?.userId === viewerId)

            return (
              <div className={s.skill} key={skill.skillId}>
                <Popover
                  content={(skill.evaluatedUsers?.edges.map(({ node }) => node) || []).map((user) => (
                    <UserHoverCard key={user.userId} email={user.email}>
                      <Avatar
                        onClick={() => history.push(routes.users.page(user.userId))}
                        style={{ cursor: 'pointer' }}
                        key={user.userId}
                        size='small'
                        src={user.avatarUrl}
                      />
                    </UserHoverCard>
                  ))}
                >
                  <Tag
                    style={{
                      fontSize: 16,
                      borderRadius: 16,
                      backgroundColor: 'unset',
                    }}
                  >
                    <div
                      style={{
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {skill.name}
                      {` +${skill.evaluatedUsers?.edges.length}`}
                      {isAgreed && (
                        <CloseOutlined
                          style={{
                            color: '#65676b',
                            marginLeft: 8,
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                          onClick={() =>
                            userSkillUpdate({
                              variables: {
                                userId,
                                skillId: skill.skillId,
                              },
                            })
                          }
                        />
                      )}
                    </div>
                  </Tag>
                </Popover>
                {!isAgreed && (
                  <LikeOutlined
                    onClick={() =>
                      userSkillUpdate({
                        variables: {
                          userId,
                          skillId: skill.skillId,
                        },
                      })
                    }
                    className={cls(s.liked, { [s.isLiked]: isAgreed })}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export const UserInfoSkill: React.FC<UserInfoProps> = ({ userId, viewerId, readonly }) => {
  const { t } = useTranslation()
  const [visible, setVisible] = React.useState(false)

  const { data } = useGetUserSkillQuery({
    variables: {
      userId,
      skillPagination: {
        last: 100,
      },
      evaluatedUserPagination: {
        last: 1000,
      },
    },
  })

  const skills = data?.user.skills?.edges.map(({ node }) => node) || []

  return (
    <UserInfoSectionLayout
      title={t('user.info.skill.title')}
      icon={<ToolOutlined />}
      showActions={true}
      actions={
        <Button
          className={s.skillAddButton}
          icon={<PlusCircleOutlined style={{ color: '#48bbb5' }} />}
          type='text'
          size='large'
          onClick={() => setVisible(true)}
        >
          {t(`user.info.skill.${readonly ? 'admitTag' : 'addTag'}`)}
        </Button>
      }
    >
      {skills.length > 0 ? (
        <UserInfoSkillInner userId={userId} viewerId={viewerId} skills={skills} />
      ) : (
        <span>添加第一条 Ta 的技能吧～</span>
      )}
      <UserInfoSkillModal
        visible={visible}
        setVisible={setVisible}
        userId={userId}
        readonly={readonly}
        userSkills={skills.map((skill) => ({
          name: skill.name,
          skillId: skill.skillId,
        }))}
      />
    </UserInfoSectionLayout>
  )
}
